import { useState, useEffect, useCallback, createContext, useContext } from 'react'
import type { Session, User, Provider } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import type { Role, UserProfile, OrgMembership } from '../types'

interface AuthState {
  session: Session | null
  user: User | null
  profile: UserProfile | null
  loading: boolean
  mustChangePassword: boolean
  signIn: (email: string, password: string) => Promise<{ error: string | null }>
  signInWithProvider: (provider: Provider) => Promise<{ error: string | null }>
  signOut: () => Promise<void>
  clearMustChangePassword: () => void
  switchOrg: (orgId: string) => void
}

const AuthContext = createContext<AuthState | null>(null)

export const AuthProvider = AuthContext.Provider

const ORG_STORAGE_KEY = 'rv-active-org'

export function useAuthState(): AuthState {
  const [session, setSession] = useState<Session | null>(null)
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [mustChangePassword, setMustChangePassword] = useState(false)

  const loadProfile = useCallback(async (currentUser: User) => {
    // Load ALL memberships for this user
    const { data } = await supabase
      .from('org_members')
      .select('role, org_id, organizations(name, mi_enabled)')
      .eq('user_id', currentUser.id)

    const rows = data ?? []
    const memberships: OrgMembership[] = rows.map((row) => {
      const orgData = row.organizations as unknown as { name: string; mi_enabled: boolean } | null
      return {
        org_id: row.org_id,
        org_name: orgData?.name ?? 'Unknown',
        role: row.role as Role,
        mi_enabled: orgData?.mi_enabled ?? false,
      }
    })

    const provider = (currentUser.app_metadata?.provider as string) ?? 'email'

    if (memberships.length === 0) {
      setProfile({
        id: currentUser.id,
        email: currentUser.email ?? '',
        role: 'viewer',
        org_id: null,
        org_name: null,
        mi_enabled: false,
        memberships: [],
        provider,
      })
      return
    }

    // Pick active org: saved preference → first rv_admin → first membership
    const savedOrgId = localStorage.getItem(ORG_STORAGE_KEY)
    let active = memberships.find(m => m.org_id === savedOrgId)
      ?? memberships.find(m => m.role === 'rv_admin')
      ?? memberships[0]

    setProfile({
      id: currentUser.id,
      email: currentUser.email ?? '',
      role: active.role,
      org_id: active.org_id,
      org_name: active.org_name,
      mi_enabled: active.mi_enabled,
      memberships,
      provider,
    })
  }, [])

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s)
      setUser(s?.user ?? null)
      if (s?.user) {
        setMustChangePassword(!!s.user.user_metadata?.must_change_password)
        loadProfile(s.user).finally(() => setLoading(false))
      } else {
        setLoading(false)
      }
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s)
      setUser(s?.user ?? null)
      if (s?.user) {
        setMustChangePassword(!!s.user.user_metadata?.must_change_password)
        loadProfile(s.user)
      } else {
        setProfile(null)
        setMustChangePassword(false)
      }
    })

    return () => subscription.unsubscribe()
  }, [loadProfile])

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    return { error: error?.message ?? null }
  }, [])

  const signInWithProvider = useCallback(async (provider: Provider) => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: window.location.origin },
    })
    return { error: error?.message ?? null }
  }, [])

  const signOut = useCallback(async () => {
    localStorage.removeItem(ORG_STORAGE_KEY)
    await supabase.auth.signOut()
    setProfile(null)
  }, [])

  const clearMustChangePassword = useCallback(() => {
    setMustChangePassword(false)
  }, [])

  const switchOrg = useCallback((orgId: string) => {
    if (!profile) return
    const membership = profile.memberships.find(m => m.org_id === orgId)
    if (!membership) return

    localStorage.setItem(ORG_STORAGE_KEY, orgId)
    setProfile({
      ...profile,
      role: membership.role,
      org_id: membership.org_id,
      org_name: membership.org_name,
      mi_enabled: membership.mi_enabled,
    })
  }, [profile])

  return { session, user, profile, loading, mustChangePassword, signIn, signInWithProvider, signOut, clearMustChangePassword, switchOrg }
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
